import React from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import {
    Badge,
    Button,
    Col,
    ConfigProvider,
    message,
    Modal,
    Popconfirm, Row,
    Space,
    Switch,
    Table,
    Tooltip
} from "antd";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import {DEFAULT_PER_PAGE} from "../../config";
import NewFirewallRulesetModal from "./NewFirewallRulesetModal";
import {displayErrors, normalizeEnum} from "../../libs/utils";
import {DateTimeFormat} from "../shared/DateTimeFormat";
import {DeleteOutlined, ExclamationCircleOutlined, EyeOutlined, FolderOpenOutlined} from "@ant-design/icons";
import {bulkDeleteFirewallRulesets, deleteFirewallRuleset, loadFirewallRulesets, updateFirewallRulesetStatus} from "../../actions/firewall";
import EditFirewallRulesetModal from "./EditFirewallRulesetModal";
import AccessGate from "../permissions/AccessGate";
import {hasPermission} from "../permissions/PermissionUtils";
import AccessItem from "../permissions/AccessItem";

class FirewallRulesets extends React.Component {
    state = {
        rulesets: [],
        loading: false,
        pagination: { current: 1, pageSize: DEFAULT_PER_PAGE, showSizeChanger: true, total: 0 },
        showNewRulesetModal: false,
        activeRuleset: '',
        showEditRulesetModal: false,
        selectedRulesets: []
    };

    componentDidMount() {
        this.props.updatePage('Firewall rulesets');
        this.loadRulesets();
    }

    loadRulesets(page = null, per_page = null) {
        if(page === null) {
            page = this.state.pagination.current;
        }

        if(per_page === null) {
            per_page = this.state.pagination.pageSize;
        }

        this.setState({ loading: true });

        this.props.loadFirewallRulesets(page, per_page, (res) => {
            this.setState({ rulesets: res.data.data, loading: false,
                pagination: { current: res.data.page, pageSize: res.data.per_page, total: res.data.total, showSizeChanger: true } });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                displayErrors(err.response.data);
                this.setState({ loading: false })
            }
        });
    }

    changePage(data) {
        this.loadRulesets(data.current, data.pageSize);
    }

    toggleFirewallRulesetStatus(guid, status) {
        let data = {
            status: status ? 'ACTIVE' : 'DISABLED'
        };

        let rulesets = this.state.rulesets.map((item) => {
            if(item.guid === guid) {
                return {...item, status: data['status']};
            }

            return item;
        });

        this.setState({ rulesets: rulesets, loading: true });

        this.props.updateFirewallRulesetStatus(guid, data, () => {
            message.success('Firewall Ruleset successfully updated!');
            this.setState({ loading: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    deleteFirewallRuleset(guid) {
        this.setState({ loading: true });

        this.props.deleteFirewallRuleset(guid, () => {
            message.success('Firewall Ruleset successfully deleted!');
            this.loadRulesets();
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        });
    }

    confirmBulkDelete() {
        const { confirm } = Modal;

        confirm({
            title: 'Are you sure you want to delete these rulesets?',
            centered: true,
            okText: 'Yes',
            okType: 'danger',
            icon: <ExclamationCircleOutlined />,
            content: 'This can have significant impact on your domains and content!',
            onOk: () => {
                this.setState({ loading: true });

                let data = {
                    rules: this.state.selectedRules
                };

                this.props.bulkDeleteFirewallRulesets(data, () => {
                    message.success('Firewall rulesets successfully deleted!');
                    this.loadRulesets();
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        this.setState({ loading: false });
                        displayErrors(err.response.data);
                    }
                });
            }
        })
    }

    render() {
        const columns = [
            { title: '', width: '50px', render: (item, record) => {
                return <Tooltip title={record.status === 'ACTIVE' ? 'Disable ruleset' : 'Enable ruleset'}>
                    <Switch size='small' disabled={!hasPermission(this.props.company.permissions, ['can_edit_firewall_rulesets'])} checked={record.status === 'ACTIVE'}
                            onChange={(e) => this.toggleFirewallRulesetStatus(record.guid, e)} />
                </Tooltip>
            }},
            { title: 'Name', dataIndex: 'name', render: (item) => <strong>{item}</strong> },
            { title: 'Created', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item} /> },
            { title: 'Status', dataIndex: 'status', render: (item) => <Badge status={item === 'ACTIVE' ? 'success' : 'error'} text={normalizeEnum(item)} /> },
            { title: '', align: 'right', render: (item, record) => {
                    return <Space>
                        <Button size='small' icon={<EyeOutlined />} onClick={() => this.setState({ activeRuleset: record.guid, showEditRulesetModal: true })}>View</Button>
                        <AccessItem scopes={['can_delete_firewall_rulesets']}>
                            <Popconfirm
                                title="Are you sure you want to delete this ruleset?"
                                onConfirm={() => this.deleteFirewallRuleset(record.guid)}
                                okText='Delete'
                                placement='topRight'
                                okButtonProps={{danger: true}}
                                cancelText='Cancel'>
                                <Tooltip title='Delete firewall ruleset'>
                                    <Button size='small' danger icon={<DeleteOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                        </AccessItem>
                    </Space>;
                }}
        ];

        return(
            <AccessGate scopes={['can_view_firewall_rulesets']}>
                <Row justify='space-between' style={{marginBottom: '10px'}}>
                    <Col span={22}>
                        <Space>
                            <AccessItem scopes={['can_add_firewall_rulesets']}>
                                <Button icon={<PlusOutlined />} onClick={() => this.setState({ showNewRulesetModal: true })}>Add Firewall Ruleset</Button>
                            </AccessItem>
                            <AccessItem scopes={['can_delete_firewall_rulesets']}>
                                <Button icon={<DeleteOutlined />} disabled={this.state.selectedRulesets.length === 0} danger onClick={() => this.confirmBulkDelete()}>Delete</Button>
                            </AccessItem>
                        </Space>
                    </Col>
                    <Col><Tooltip title='Reload table'><Button type='text' icon={<UndoOutlined />} onClick={() => this.loadRulesets()}/></Tooltip></Col>
                </Row>

                <ConfigProvider renderEmpty={() => <div style={{textAlign: 'center'}}>
                    <FolderOpenOutlined style={{fontSize: 40}}/>
                    <p>Nothing to see here...</p>
                    <AccessItem scopes={['can_add_firewall_rulesets']}>
                        <Button icon={<PlusOutlined/>} onClick={() => this.setState({showNewRulesetModal: true})}>Add Firewall Ruleset</Button>
                    </AccessItem>
                </div>}>
                    <div style={{overflowX: 'auto'}}>
                        <Table
                            dataSource={this.state.rulesets}
                            rowSelection={{
                                type: 'checkbox',
                                onChange: (rulesets) => this.setState({selectedRulesets: rulesets})
                            }}
                            columns={columns}
                            rowKey={(item) => item.guid}
                            pagination={this.state.pagination}
                            onChange={(e) => this.changePage(e)}
                            loading={this.state.loading}/>
                    </div>
                </ConfigProvider>

                <NewFirewallRulesetModal
                    showModal={this.state.showNewRulesetModal}
                    reloadRulesets={() => this.loadRulesets()}
                    close={() => this.setState({ showNewRulesetModal: false })} />

                <EditFirewallRulesetModal
                    showModal={this.state.showEditRulesetModal}
                    reloadRulesets={() => this.loadRulesets()}
                    guid={this.state.activeRuleset}
                    close={() => this.setState({ showEditRulesetModal: false })} />
            </AccessGate>
        );
    }
}

const mapStateToProps = state => ({
    company: state.auth.user.user.company
});

export default connect(mapStateToProps, { updatePage, loadFirewallRulesets,
    updateFirewallRulesetStatus, deleteFirewallRuleset, bulkDeleteFirewallRulesets })(FirewallRulesets);